/**
 * ClickableCard — a GlassSurface that is also interactive.
 *
 * Wraps the card in a button-like element with:
 *   - haptic feedback on tap
 *   - a subtle press-scale animation
 *   - an optional onClick handler for navigation / modal opening
 */
import { useCallback, type ReactNode, type CSSProperties } from 'react';
import { motion } from 'motion/react';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import { cn } from '@/lib/cn';

interface ClickableCardProps {
  /** Tone accent — used for glow colour resolution */
  tone?: 'lunar' | 'plasma' | 'nebula';
  /** Called when the card is tapped */
  onClick?: () => void;
  /** Disable the press animation (for nested cards) */
  noPress?: boolean;
  /** Extra variants — 'strong' uses thicker fill like lg-surface-strong */
  variant?: 'regular' | 'strong';
  /** Optional coloured outer glow ring */
  glow?: false | 'lunar' | 'plasma' | 'nebula';
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

const glowColor: Record<string, string> = {
  lunar: 'var(--accent-primary)',
  plasma: 'var(--accent-secondary)',
  nebula: 'var(--accent-tertiary)',
};

export function ClickableCard({
  onClick,
  noPress = false,
  variant = 'regular',
  glow = false,
  className,
  style,
  children,
}: ClickableCardProps) {
  const { haptic } = useTelegramWebApp();
  const sounds = useUISounds();

  const handleCardTap = useCallback(() => {
    if (onClick) {
      haptic.light();
      sounds.cardPress();
      onClick();
    }
  }, [onClick, haptic, sounds]);

  const isInteractive = Boolean(onClick);

  const glowStyle: CSSProperties = glow
    ? (() => {
        const c = glowColor[glow];
        return {
          boxShadow: `
            var(--glass-shadow),
            var(--glass-inner-glow),
            0 0 0 1px color-mix(in oklab, ${c} 55%, transparent),
            0 0 48px -8px color-mix(in oklab, ${c} 55%, transparent)
          `,
        };
      })()
    : {};

  return (
    <motion.div
      onClick={isInteractive ? handleCardTap : undefined}
      data-no-sound={isInteractive ? 'true' : undefined}
      whileTap={isInteractive && !noPress ? { scale: 0.985 } : undefined}
      transition={{ duration: 0.15 }}
      className={cn(
        'lg-surface relative overflow-hidden rounded-[var(--radius-card)]',
        variant === 'strong' && 'lg-surface-strong lg-sheen',
        isInteractive && 'cursor-pointer',
        className,
      )}
      style={{ ...glowStyle, ...style }}
    >
      <div className="relative z-[1]">{children}</div>
    </motion.div>
  );
}
